import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import type { AccessibilityIssue, Severity } from '../utils/accessibilityChecker';

export interface AccessibilityPreflightDialogProps {
  open: boolean;
  /** Slide order — used so the dialog can list slides in deck order. */
  slideOrder: Array<{ id: string; index: number }>;
  /** Issues keyed by slide id. */
  issuesBySlide: Record<string, AccessibilityIssue[]>;
  /** User has chosen to review — caller should jump to the given slide and close. */
  onReview: (slideId: string) => void;
  /** User has chosen to export anyway. */
  onExportAnyway: () => void;
  onCancel: () => void;
}

const AccessibilityPreflightDialog: React.FC<AccessibilityPreflightDialogProps> = ({
  open,
  slideOrder,
  issuesBySlide,
  onReview,
  onExportAnyway,
  onCancel,
}) => {
  const rows = slideOrder
    .map(({ id, index }) => ({ slideId: id, index, issues: issuesBySlide[id] ?? [] }))
    .filter((row) => row.issues.length > 0);

  const totalIssues = rows.reduce((sum, row) => sum + row.issues.length, 0);
  const failIssues = rows.reduce(
    (sum, row) => sum + row.issues.filter((issue) => issue.severity === 'fail').length,
    0
  );
  const warnIssues = totalIssues - failIssues;
  const visibleRows = rows.slice(0, 4);
  const hiddenSlideCount = rows.length - visibleRows.length;

  const firstSlideId = rows[0]?.slideId;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Quick accessibility check</DialogTitle>
      <DialogContent dividers>
        {rows.length === 0 ? (
          <Typography variant="body2">No accessibility issues detected.</Typography>
        ) : (
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Export can continue now. These checks are suggestions to review when you have time.
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  size="small"
                  color="default"
                  label={`${rows.length} affected ${rows.length === 1 ? 'slide' : 'slides'}`}
                />
                {failIssues > 0 && (
                  <Chip
                    size="small"
                    color="error"
                    variant="outlined"
                    label={`${failIssues} contrast ${failIssues === 1 ? 'issue' : 'issues'}`}
                  />
                )}
                {warnIssues > 0 && (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`${warnIssues} ${warnIssues === 1 ? 'warning' : 'warnings'}`}
                  />
                )}
              </Stack>
            </Box>

            <List disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {visibleRows.map((row) => {
                const worst: Severity = row.issues.some((i) => i.severity === 'fail')
                  ? 'fail'
                  : 'warn';
                return (
                  <ListItem key={row.slideId} disablePadding divider>
                    <ListItemButton onClick={() => onReview(row.slideId)}>
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        sx={{ width: '100%' }}
                      >
                        {worst === 'fail' ? (
                          <ErrorOutlineIcon fontSize="small" color="error" />
                        ) : (
                          <WarningAmberIcon fontSize="small" color="warning" />
                        )}
                        <ListItemText
                          primary={`Slide ${row.index + 1}`}
                          secondary={`${row.issues.length} ${row.issues.length === 1 ? 'check' : 'checks'} to review`}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Review
                        </Typography>
                      </Stack>
                    </ListItemButton>
                  </ListItem>
                );
              })}
              {hiddenSlideCount > 0 && (
                <ListItem>
                  <ListItemText
                    primary={`${hiddenSlideCount} more ${hiddenSlideCount === 1 ? 'slide' : 'slides'} with checks`}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
              )}
            </List>

            {firstSlideId && (
              <Button
                variant="text"
                size="small"
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => onReview(firstSlideId)}
              >
                Review first issue
              </Button>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onExportAnyway} variant="contained">
          Export to Google Slides
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccessibilityPreflightDialog;
